/** Alert rules, the firing log, and the AI summaries screen. */
const en = {
  title: 'Alerts',
  subtitle: 'Fire a notification when frustration, errors or rage clicks cross a threshold.',
  evaluating: 'Evaluating…',
  evaluateNow: 'Evaluate now',
  newRule: 'New rule',
  evaluated: (n: number) => `Evaluation complete. ${n} alert(s) fired.`,
  rules: 'Rules',
  recent: 'Recent alerts',
  inApp: 'In-app',
  noRules: 'No rules yet. Create one to start monitoring.',
  columns: { when: 'When', rule: 'Rule', detail: 'Detail' },
  noEvents: 'No alerts have fired yet.',
  ruleSummary: (metric: string, threshold: number, window: string, cooldown: string) =>
    `${metric} ≥ ${threshold} · window ${window} · cooldown ${cooldown}`,
  metrics: {
    HIGH_FRUSTRATION_SESSIONS: 'High-frustration sessions',
    ERROR_OCCURRENCES: 'Error occurrences',
    RAGE_SESSIONS: 'Sessions with rage clicks',
    NEW_ERROR_GROUPS: 'New error types',
  } as Record<string, string>,
  dialog: {
    edit: 'Edit rule',
    create: 'New alert rule',
    name: 'Name',
    namePlaceholder: 'Checkout frustration spike',
    metric: 'Metric',
    threshold: 'Threshold (≥)',
    window: 'Window',
    cooldown: 'Cooldown',
    none: 'none',
    notify: 'Notify',
    inApp: 'In-app (bell)',
    providerHint: 'Slack / Teams fire only where that integration is configured on a project.',
    followUp: 'Follow-up',
    openTicketIn: 'Also open a ticket in',
    openTicketHint:
      'Each firing opens a tracked issue there, for alerts that deserve more than a ping.',
    noTicket: "Don't open a ticket",
    saving: 'Saving…',
    save: 'Save rule',
  },
  summaries: {
    title: 'AI Summaries',
    subtitle: 'Plain-language recaps of what happened in each session.',
    configure: 'Configure AI',
    generating: 'Generating…',
    generateMore: 'Generate more',
    failed: 'Couldn’t generate summaries. Please try again.',
    aiSettings: 'AI settings',
    partial: (ok: number, failed: number, err: string) =>
      `Generated ${ok}, but ${failed} failed: ${err}`,
    error: (err: string) => `Couldn’t generate summaries: ${err}`,
    generated: (n: number) => `Generated ${n} ${n === 1 ? 'summary' : 'summaries'}.`,
    nothingNew: 'Nothing new to summarize. Every recent session already has a summary.',
    frustration: (n: number) => `${n}% frustration`,
    watch: 'Watch',
    ticket: 'Ticket',
    emptyTitle: 'No summaries yet',
    emptyBody: 'Generate AI recaps for your most recent sessions.',
    emptyCta: 'Generate summaries',
  },
};

const tr: typeof en = {
  title: 'Uyarılar',
  subtitle: 'Zorlanma, hata ya da öfke tıklamaları bir eşiği aştığında bildirim gönderin.',
  evaluating: 'Değerlendiriliyor…',
  evaluateNow: 'Şimdi değerlendir',
  newRule: 'Yeni kural',
  evaluated: (n: number) => `Değerlendirme tamam, ${n} uyarı tetiklendi.`,
  rules: 'Kurallar',
  recent: 'Son uyarılar',
  inApp: 'Uygulama içi',
  noRules: 'Henüz kural yok. İzlemeye başlamak için bir tane oluşturun.',
  columns: { when: 'Ne zaman', rule: 'Kural', detail: 'Ayrıntı' },
  noEvents: 'Henüz uyarı tetiklenmedi.',
  ruleSummary: (metric: string, threshold: number, window: string, cooldown: string) =>
    `${metric} ≥ ${threshold} · pencere ${window} · bekleme ${cooldown}`,
  metrics: {
    HIGH_FRUSTRATION_SESSIONS: 'Yüksek zorlanmalı oturumlar',
    ERROR_OCCURRENCES: 'Hata görülmeleri',
    RAGE_SESSIONS: 'Öfke tıklaması olan oturumlar',
    NEW_ERROR_GROUPS: 'Yeni hata türleri',
  },
  dialog: {
    edit: 'Kuralı düzenle',
    create: 'Yeni uyarı kuralı',
    name: 'Ad',
    namePlaceholder: 'Ödeme adımında zorlanma sıçraması',
    metric: 'Metrik',
    threshold: 'Eşik (≥)',
    window: 'Pencere',
    cooldown: 'Bekleme',
    none: 'yok',
    notify: 'Bildirim',
    inApp: 'Uygulama içi (zil)',
    providerHint:
      'Slack / Teams yalnızca o entegrasyonun yapılandırıldığı projelerde tetiklenir.',
    followUp: 'Takip',
    openTicketIn: 'Ayrıca şurada talep aç',
    openTicketHint:
      'Her tetiklenmede orada izlenen bir kayıt açılır. Bir bildirimden fazlasını hak eden uyarılar için.',
    noTicket: 'Talep açma',
    saving: 'Kaydediliyor…',
    save: 'Kuralı kaydet',
  },
  summaries: {
    title: 'AI Özetleri',
    subtitle: 'Her oturumda ne olduğunun sade dille özeti.',
    configure: 'AI ayarları',
    generating: 'Üretiliyor…',
    generateMore: 'Daha fazla üret',
    failed: 'Özetler üretilemedi. Lütfen tekrar deneyin.',
    aiSettings: 'AI ayarları',
    partial: (ok: number, failed: number, err: string) =>
      `${ok} üretildi, ${failed} başarısız: ${err}`,
    error: (err: string) => `Özetler üretilemedi: ${err}`,
    generated: (n: number) => `${n} özet üretildi.`,
    nothingNew: 'Özetlenecek yeni bir şey yok. Son oturumların hepsinin özeti var.',
    frustration: (n: number) => `%${n} zorlanma`,
    watch: 'İzle',
    ticket: 'Talep',
    emptyTitle: 'Henüz özet yok',
    emptyBody: 'En son oturumlarınız için AI özetleri üretin.',
    emptyCta: 'Özetleri üret',
  },
};

export const alerts = { en, tr };
